import React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle, ArrowUpRight, CalendarClock, CheckCircle2, ChevronRight,
  Circle, Flag, Layers, Scale, Sparkles, Target, TrendingDown,
} from "lucide-react";
import { fmtMoney } from "@/lib/currency";

/**
 * OverviewDerived — the read-only Entity Overview.
 * Everything in this view is derived server-side from canonical records
 * (projects, tasks, calendar, notes, expenditures). No duplicate manual
 * upkeep. The only manual fields are objective / description / status —
 * strategic content that cannot reasonably be inferred.
 */

const PRIORITY_COLOR = {
  Critical: "#EF4444", High: "#F59E0B", Medium: "#3B82F6",
  Normal: "#3B82F6", Low: "#94A3B8",
};

const KIND_META = {
  task: { icon: Circle, color: "#A78BFA", label: "Task" },
  project: { icon: Layers, color: "#22D3EE", label: "Project" },
  milestone: { icon: Flag, color: "#22D3EE", label: "Milestone" },
  meeting: { icon: CalendarClock, color: "#3B82F6", label: "Meeting" },
  decision: { icon: Scale, color: "#F59E0B", label: "Decision" },
  project_update: { icon: Layers, color: "#22D3EE", label: "Project" },
};

function fmtDate(iso, { withTime = false } = {}) {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return withTime
    ? d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function relativeDate(iso) {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  const diffMs = d.getTime() - Date.now();
  const days = Math.round(diffMs / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  if (days > 1 && days <= 7) return `in ${days} days`;
  if (days < -1 && days >= -7) return `${-days} days ago`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function priorityLabel(p) {
  if (!p) return null;
  return (
    <span
      className="inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border"
      style={{ color: PRIORITY_COLOR[p], borderColor: `${PRIORITY_COLOR[p]}55`, backgroundColor: `${PRIORITY_COLOR[p]}12` }}
    >
      {p}
    </span>
  );
}

function itemLinkFor(item, entityId) {
  const kind = item.kind;
  if (kind === "task") return `/entity/${entityId}?tab=tasks`;
  if (kind === "milestone" || kind === "meeting") return `/entity/${entityId}?tab=calendar`;
  if (kind === "project" || kind === "project_update") return `/entity/${entityId}?tab=projects`;
  if (kind === "decision") return `/entity/${entityId}?tab=notes`;
  return `/entity/${entityId}`;
}

function HeroCard({ overview, entityId }) {
  const stage = overview.current_stage;
  const priority = overview.current_priority;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="overview-hero">
      <div className="p-6 rounded-xl border border-white/10 bg-[#151A23]">
        <div className="flex items-center gap-2 text-eyebrow mb-3">
          <Sparkles className="w-3 h-3" /> Current stage
        </div>
        {stage ? (
          <div className="font-display text-xl md:text-2xl leading-tight text-white/95" data-testid="overview-stage">
            {stage}
          </div>
        ) : (
          <div className="text-sm text-white/40">No active projects yet — add a project and its phase will appear here automatically.</div>
        )}
        <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-white/50">
          <StatChip label="active projects" value={overview.active_project_count}/>
          <StatChip label="open tasks" value={overview.open_task_count}/>
          {overview.overdue_task_count > 0 && (
            <StatChip label="overdue" value={overview.overdue_task_count} accent="#EC4899"/>
          )}
        </div>
      </div>

      <div className="p-6 rounded-xl border border-white/10 bg-[#151A23]">
        <div className="flex items-center gap-2 text-eyebrow mb-3">
          <Target className="w-3 h-3" /> Current priority
        </div>
        {priority ? (
          <Link
            to={itemLinkFor(priority, entityId)}
            className="group block"
            data-testid="overview-current-priority"
          >
            <div className="flex items-center gap-2 mb-2">
              {priorityLabel(priority.priority)}
              {priority.overdue && (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-300">Overdue</span>
              )}
              <span className="text-[10px] uppercase tracking-wider text-white/40">{KIND_META[priority.kind]?.label || priority.kind}</span>
            </div>
            <div className="font-display text-lg leading-snug text-white/95 group-hover:text-white flex items-start gap-2">
              <span className="flex-1">{priority.title}</span>
              <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-white/90 flex-shrink-0 mt-1"/>
            </div>
            {priority.due_date && (
              <div className="text-xs text-white/50 mt-1">Due {relativeDate(priority.due_date)}</div>
            )}
          </Link>
        ) : (
          <div className="text-sm text-white/40">Nothing pressing. Add a task or set a project deadline and the priority will surface here.</div>
        )}
      </div>
    </div>
  );
}

function StatChip({ label, value, accent = "#94A3B8" }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border"
      style={{ borderColor: `${accent}33`, color: `${accent}`, backgroundColor: `${accent}0F` }}
    >
      <span className="tabular-nums font-semibold text-white/90">{value}</span>
      <span className="uppercase tracking-wider text-[10px] text-white/50">{label}</span>
    </span>
  );
}

function WhatMattersNow({ items, entityId }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="overview-what-matters">
      <div className="text-eyebrow mb-3">What matters now</div>
      <div className="divide-y divide-white/5">
        {items.map((it) => {
          const meta = KIND_META[it.kind] || KIND_META.task;
          const Icon = meta.icon;
          const when = it.starts_at
            ? fmtDate(it.starts_at, { withTime: true })
            : it.due_date
              ? relativeDate(it.due_date)
              : it.date ? fmtDate(it.date) : "";
          return (
            <Link
              key={`${it.kind}-${it.id}`}
              to={itemLinkFor(it, entityId)}
              className="flex items-center gap-3 py-3 group hover:bg-white/[0.02] -mx-2 px-2 rounded"
              data-testid={`overview-matters-${it.kind}-${it.id}`}
            >
              <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: meta.color }}/>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white/90 truncate">{it.title}</div>
                <div className="text-[11px] text-white/40 flex items-center gap-2 mt-0.5">
                  <span className="uppercase tracking-wider">{meta.label}</span>
                  {when && <span>· {when}</span>}
                  {it.priority && <span>· {it.priority}</span>}
                  {it.overdue && <span className="text-red-300 uppercase tracking-wider">· Overdue</span>}
                </div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white/70 flex-shrink-0"/>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Financials({ financials, entityId }) {
  if (!financials) return null;
  const byCcy = financials.by_currency || [];
  const isMulti = byCcy.length > 1;
  return (
    <div className="p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="overview-financials">
      <div className="flex items-center justify-between mb-3">
        <div className="text-eyebrow">Financial snapshot</div>
        <Link to={`/entity/${entityId}?tab=budget`} className="text-[11px] uppercase tracking-wider text-white/40 hover:text-white/90 inline-flex items-center gap-1">
          Open budget <ChevronRight className="w-3 h-3"/>
        </Link>
      </div>
      {isMulti ? (
        <div className="space-y-3" data-testid="overview-financials-multi">
          {byCcy.map((b) => {
            const over = Number(b.spent) > Number(b.approved_budget) && Number(b.approved_budget) > 0;
            return (
              <div key={b.currency} className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="font-display text-sm font-semibold text-white">{b.currency}</div>
                  {over && <TrendingDown className="w-3.5 h-3.5 text-amber-300"/>}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <FinCell label="Approved" value={fmtMoney(b.approved_budget, b.currency)}/>
                  <FinCell label="Project budgeted" value={fmtMoney(b.project_budget, b.currency)}/>
                  <FinCell label="Spent" value={fmtMoney(b.spent, b.currency)} accent={over}/>
                  <FinCell label="Remaining" value={fmtMoney(b.remaining, b.currency)} accent={Number(b.remaining) < 0}/>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <FinCell label={`Entity approved (${financials.currency})`} value={fmtMoney(financials.approved_budget, financials.currency)}/>
          <FinCell label="Project budgeted" value={fmtMoney(financials.project_budget, financials.currency)}/>
          <FinCell label="Spent" value={fmtMoney(financials.spent, financials.currency)}
                   accent={Number(financials.spent) > Number(financials.approved_budget) && Number(financials.approved_budget) > 0}/>
          <FinCell label="Remaining" value={fmtMoney(financials.remaining, financials.currency)}
                   accent={Number(financials.remaining) < 0}/>
        </div>
      )}
    </div>
  );
}

function FinCell({ label, value, accent }) {
  return (
    <div>
      <div className="text-white/40 text-[10px] uppercase tracking-wider">{label}</div>
      <div className={`mt-0.5 tabular-nums ${accent ? "text-amber-300" : "text-white/90"}`}>{value}</div>
    </div>
  );
}

function RecentActivity({ items, entityId }) {
  if (!items || items.length === 0) return (
    <div className="p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="overview-recent">
      <div className="text-eyebrow mb-3">Recent activity</div>
      <div className="text-sm text-white/40">Nothing recorded yet.</div>
    </div>
  );
  return (
    <div className="p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="overview-recent">
      <div className="text-eyebrow mb-3">Recent activity</div>
      <div className="divide-y divide-white/5">
        {items.map((it) => {
          const meta = KIND_META[it.kind] || KIND_META.task;
          const Icon = it.kind === "task" && it.status === "Done" ? CheckCircle2 : meta.icon;
          const secondary = [
            meta.label,
            it.status && it.status !== "Done" ? it.status : null,
            it.resolved ? "Resolved" : null,
            it.when ? relativeDate(it.when.slice(0, 10)) : null,
          ].filter(Boolean).join(" · ");
          return (
            <Link
              key={`${it.kind}-${it.id}`}
              to={itemLinkFor(it, entityId)}
              className="flex items-center gap-3 py-2.5 group hover:bg-white/[0.02] -mx-2 px-2 rounded"
              data-testid={`overview-recent-${it.kind}-${it.id}`}
            >
              <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: meta.color }}/>
              <div className="flex-1 min-w-0">
                <div className={`text-sm truncate ${it.status === "Done" ? "line-through text-white/50" : "text-white/90"}`}>
                  {it.title}
                </div>
                <div className="text-[11px] text-white/40">{secondary}</div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white/70 flex-shrink-0"/>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ObjectiveBlock({ overview, entityId, onEdit }) {
  const { objective, description } = overview.entity || {};
  if (!objective && !description) {
    return (
      <button
        onClick={onEdit}
        className="w-full p-5 rounded-xl border border-dashed border-white/15 bg-white/[0.02] text-left text-sm text-white/50 hover:text-white/80 hover:border-white/30 transition-colors"
        data-testid="overview-objective-empty"
      >
        Add a main objective for this entity — that's the one manual piece the Overview needs.
      </button>
    );
  }
  return (
    <div className="p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="overview-objective">
      <div className="flex items-center justify-between mb-2">
        <div className="text-eyebrow">Main objective</div>
        <button
          onClick={onEdit}
          className="text-[11px] uppercase tracking-wider text-white/40 hover:text-white/90"
          data-testid="overview-objective-edit"
        >
          Edit
        </button>
      </div>
      {objective && <div className="font-display text-lg text-white/95 leading-snug mb-2">{objective}</div>}
      {description && <div className="text-sm text-white/60 leading-relaxed">{description}</div>}
    </div>
  );
}

export default function OverviewDerived({ overview, entityId, onEditEntity, loading }) {
  if (loading || !overview) {
    return (
      <div className="grid gap-4" data-testid="overview-panel-loading">
        {[1,2,3].map((i) => (
          <div key={i} className="h-32 rounded-xl border border-white/10 bg-white/[0.02] animate-pulse"/>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="overview-panel">
      <ObjectiveBlock overview={overview} entityId={entityId} onEdit={onEditEntity}/>
      <HeroCard overview={overview} entityId={entityId}/>
      <WhatMattersNow items={overview.what_matters_now} entityId={entityId}/>
      <Financials financials={overview.financials} entityId={entityId}/>
      <RecentActivity items={overview.recent_activity} entityId={entityId}/>
      <div className="text-[10px] text-white/30 text-center pt-2">
        Everything above is derived automatically from projects, tasks, calendar, notes and expenditures. No manual upkeep needed.
      </div>
    </div>
  );
}

export { AlertCircle }; // noop — silences eslint unused warnings in dev
